import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Search, Copy, Sparkles, ChevronRight, AlertCircle, FileEdit, ClipboardCheck, UserPlus, Send } from "lucide-react";
import { api, errMsg, fmtWindow, usd } from "../../lib/api";
import { Btn, Card, Empty, ServiceIcon, StatusChip, Money, Select, Input } from "../../components/kit";

const TABS = [
  ["all", "All", () => true],
  ["open", "Open", (j) => ["draft", "open"].includes(j.status)],
  ["assigned", "Assigned", (j) => j.status === "assigned"],
  ["active", "Active", (j) => ["en_route", "before_check", "working", "after_check"].includes(j.status)],
  ["review", "Review", (j) => j.status === "awaiting_review"],
  ["done", "Done", (j) => ["completed", "cancelled"].includes(j.status)],
];
const ACTIVE = ["assigned", "en_route", "before_check", "working", "after_check", "awaiting_review"];

const Stat = ({ label, value, green, testid }) => (
  <div className="py-3" data-testid={testid}>
    <div className="text-sm text-neutral-500">{label}</div>
    <div className={`text-3xl font-semibold font-mono mt-1 ${green ? "text-[#10B981]" : ""}`}>{value}</div>
  </div>
);

function Helper({ jobs, pendingSpecs, setTab, nav }) {
  const needs = jobs.filter((j) => j.status === "open" && j.dispatch?.needs_dispatch).length;
  const drafts = jobs.filter((j) => j.status === "draft").length;
  const review = jobs.filter((j) => j.status === "awaiting_review").length;
  const tips = [
    needs && [AlertCircle, `${needs} job${needs > 1 ? "s" : ""} had no acceptance — assign someone`, () => setTab("open"), "text-red-400"],
    review && [ClipboardCheck, `${review} job${review > 1 ? "s" : ""} waiting for your review`, () => setTab("review"), "text-purple-300"],
    drafts && [FileEdit, `${drafts} draft${drafts > 1 ? "s" : ""} — publish once the customer approves`, () => setTab("open"), "text-amber-400"],
    pendingSpecs && [UserPlus, `${pendingSpecs} specialist application${pendingSpecs > 1 ? "s" : ""} to approve`, () => nav("/a/specialists"), "text-[#FF6B00]"],
  ].filter(Boolean);
  if (!tips.length) return null;
  return (
    <div className="space-y-2" data-testid="assistant-tips">
      {tips.map(([I, text, go, color], i) => (
        <button key={i} onClick={go} className="w-full flex items-center gap-3 rounded-2xl border border-[#262626] bg-[#111] px-4 py-3 text-left text-sm hover:border-[#333]" data-testid="assistant-tip">
          <I className={`w-4 h-4 shrink-0 ${color}`} />
          <span className="flex-1">{text}</span>
          <ChevronRight className="w-4 h-4 text-neutral-600" />
        </button>
      ))}
    </div>
  );
}

function JobCard({ j, specs, reload }) {
  const nav = useNavigate();
  const [busy, setBusy] = useState("");
  const run = async (key, fn, msg) => {
    setBusy(key);
    try {
      await fn();
      toast.success(msg);
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy("");
    }
  };
  const assign = (id) => id && run("asg", () => api.post(`/admin/jobs/${j.id}/assign`, { specialist_id: id, reason: "Assigned from job board" }), "Assigned");
  const copyOffer = async () => {
    const text = `Driven Auto Care job #${j.number}\n${j.service_name} · ${j.vehicle_label}\nArea: ${[j.customer.city, j.customer.zip].filter(Boolean).join(" ")}\nWhen: ${fmtWindow(j.window_start, j.window_end)}\nPayout: ${usd(j.pricing.payout)}\nOpen the Driven Auto Care app to accept.`;
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Offer copied — no customer contact included");
    } catch {
      toast.error("Copy not allowed in this browser");
    }
  };
  const canAssign = ["open", ...ACTIVE.slice(0, 5)].includes(j.status);
  const p = j.pricing;
  return (
    <Card className="space-y-4" data-testid="board-job-card">
      <button className="w-full text-left" onClick={() => nav(`/a/jobs/${j.id}`)} data-testid="board-job-open">
        <div className="flex items-start gap-3">
          <ServiceIcon k={j.service_key} />
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-lg leading-tight">{j.service_name}{j.is_demo && <span className="text-[10px] font-mono text-purple-300 border border-purple-800 rounded px-1 ml-2 align-middle">DEMO</span>}</div>
            <div className="text-sm text-neutral-400">#{j.number} · {j.vehicle_label}</div>
            <div className="text-xs text-neutral-500 mt-1">{[j.customer.city, j.customer.zip].filter(Boolean).join(" ")} · {fmtWindow(j.window_start, j.window_end)}</div>
          </div>
          <StatusChip status={j.status} needs={j.status === "open" && j.dispatch?.needs_dispatch} />
        </div>
      </button>
      <div className="grid grid-cols-3 gap-2">
        <div><div className="text-xs text-neutral-500">Customer</div><Money v={p.subtotal} className="text-lg" /></div>
        <div><div className="text-xs text-neutral-500">Specialist</div><Money v={p.payout} className="text-lg" /></div>
        <div><div className="text-xs text-neutral-500">Driven keeps*</div><Money v={p.driven_remaining} green={p.driven_remaining >= 0} className={`text-lg ${p.driven_remaining < 0 ? "text-red-400" : ""}`} testid="board-driven-remaining" /></div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        {canAssign ? (
          <Select value={j.assigned_to || ""} onChange={(e) => assign(e.target.value)} disabled={busy === "asg"} data-testid="board-assign-select">
            <option value="">{j.assigned_to ? j.assigned_name : j.pending_offers ? `${j.pending_offers} offer(s) out` : "Unassigned"}</option>
            {specs.filter((s) => s.id !== j.assigned_to && s.categories?.includes(j.category)).map((s) => <option key={s.id} value={s.id}>{s.name}{s.available ? " · Available" : ""}</option>)}
          </Select>
        ) : (
          <div className="h-11 flex items-center text-sm text-neutral-400 px-1">{j.assigned_name || (j.status === "draft" ? "Not published yet" : "—")}</div>
        )}
        {j.status === "draft" ? (
          <Btn onClick={() => nav(`/a/jobs/${j.id}`)} data-testid="board-publish-btn"><Send className="w-4 h-4" />Review & publish</Btn>
        ) : j.status === "awaiting_review" ? (
          <Btn variant="success" onClick={() => nav(`/a/jobs/${j.id}`)} data-testid="board-review-btn"><ClipboardCheck className="w-4 h-4" />Review</Btn>
        ) : j.status === "open" ? (
          <Btn variant="outline" onClick={copyOffer} data-testid="board-copy-offer-btn"><Copy className="w-4 h-4" />Copy offer</Btn>
        ) : (
          <Btn variant="outline" onClick={() => nav(`/a/jobs/${j.id}`)} data-testid="board-view-job-btn">View job<ChevronRight className="w-4 h-4" /></Btn>
        )}
      </div>
    </Card>
  );
}

export default function Board() {
  const nav = useNavigate();
  const [jobs, setJobs] = useState(null);
  const [specs, setSpecs] = useState([]);
  const [tab, setTab] = useState("all");
  const [demo, setDemo] = useState("");
  const [q, setQ] = useState("");
  const [seeding, setSeeding] = useState(false);
  const load = useCallback(async () => {
    try {
      const [j, s] = await Promise.all([api.get("/admin/jobs", { params: { demo: demo || undefined } }), api.get("/admin/specialists")]);
      setJobs(j.data);
      setSpecs(s.data);
    } catch {
      /* retry on next poll */
    }
  }, [demo]);
  useEffect(() => {
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [load]);
  const approved = specs.filter((s) => s.status === "approved");
  const stats = useMemo(() => {
    const all = jobs || [];
    const done = all.filter((j) => j.status === "completed");
    return {
      active: all.filter((j) => ACTIVE.includes(j.status)).length,
      open: all.filter((j) => ["draft", "open"].includes(j.status)).length,
      sales: done.reduce((a, j) => a + j.pricing.subtotal, 0),
      kept: done.reduce((a, j) => a + j.pricing.driven_remaining, 0),
    };
  }, [jobs]);
  const filter = TABS.find((t) => t[0] === tab)[2];
  const list = (jobs || []).filter(filter).filter((j) => !q || `${j.number} ${j.customer.name} ${j.vehicle_label} ${j.service_name} ${j.customer.zip}`.toLowerCase().includes(q.toLowerCase()));
  const seed = async () => {
    setSeeding(true);
    try {
      await api.post("/admin/demo/jobs");
      toast.success("3 DEMO jobs added — try assigning one");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSeeding(false);
    }
  };
  return (
    <div className="space-y-5 fade-in" data-testid="admin-board">
      <div className="grid grid-cols-2 gap-x-6 border-b border-[#1f1f1f] pb-3">
        <Stat label="Active jobs" value={stats.active} testid="stat-active" />
        <Stat label="Open jobs" value={stats.open} testid="stat-open" />
        <Stat label="Completed sales" value={usd(stats.sales)} testid="stat-sales" />
        <Stat label="Driven kept*" value={usd(stats.kept)} green testid="stat-kept" />
      </div>
      <Helper jobs={jobs || []} pendingSpecs={specs.filter((s) => s.status === "pending").length} setTab={setTab} nav={nav} />
      <div className="flex items-center justify-between">
        <h1 className="font-head text-3xl uppercase font-bold">Job board</h1>
        <Btn onClick={() => nav("/a/jobs/new")} className="rounded-full" data-testid="board-new-job-btn"><Plus className="w-4 h-4" />New job</Btn>
      </div>
      <div className="flex gap-1 overflow-x-auto -mx-4 px-4">
        {TABS.map(([k, l, f]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`board-tab-${k}`}
            className={`h-9 px-4 rounded-full text-sm whitespace-nowrap ${tab === k ? "bg-[#1f1f1f] text-white" : "text-neutral-500"}`}>
            {l}{jobs && k !== "all" ? <span className="ml-1 text-xs text-neutral-600">{jobs.filter(f).length}</span> : null}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-3 gap-2">
        <div className="col-span-2 relative">
          <Search className="w-4 h-4 absolute left-3 top-3.5 text-neutral-600" />
          <Input className="pl-9" placeholder="Search name, car, ZIP" value={q} onChange={(e) => setQ(e.target.value)} data-testid="board-search-input" />
        </div>
        <Select value={demo} onChange={(e) => setDemo(e.target.value)} data-testid="board-demo-filter">
          <option value="">All data</option><option value="hide">Real only</option><option value="only">Demo only</option>
        </Select>
      </div>
      {jobs === null && <div className="h-40 animate-pulse bg-[#141414] rounded-2xl" />}
      {jobs && !jobs.length && !demo && (
        <Card className="text-center space-y-3 py-8" data-testid="board-empty">
          <Sparkles className="w-7 h-7 mx-auto text-[#FF6B00]" />
          <div className="font-medium">No jobs yet</div>
          <p className="text-sm text-neutral-500 max-w-xs mx-auto">Enter your first job from a customer call or text, or load sample DEMO jobs to try assigning and reviewing.</p>
          <div className="flex flex-col sm:flex-row gap-2 justify-center">
            <Btn onClick={() => nav("/a/jobs/new")} data-testid="empty-new-job-btn"><Plus className="w-4 h-4" />New job</Btn>
            <Btn variant="outline" onClick={seed} loading={seeding} data-testid="load-demo-jobs-btn">Load 3 DEMO jobs</Btn>
          </div>
        </Card>
      )}
      {jobs && (jobs.length > 0 || demo) && !list.length && <Empty title="Nothing here" text="Try another tab." />}
      {list.map((j) => <JobCard key={j.id} j={j} specs={approved} reload={load} />)}
      {jobs && jobs.length > 0 && <p className="text-[11px] text-neutral-600">*Driven keeps = customer subtotal − parts − specialist payout − other costs. Before overhead and taxes.</p>}
    </div>
  );
}
